// Compression settings manager

import type { CompressionConfig, CompressionMode } from '../types/imageCompression';
import { DEFAULT_COMPRESSION_CONFIG } from '../types/imageCompression';

const STORAGE_KEY = 'image_compression_settings';

/**
 * Load compression settings from localStorage
 */
export function loadCompressionSettings(): CompressionConfig {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_COMPRESSION_CONFIG, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load compression settings:', error);
  }
  return DEFAULT_COMPRESSION_CONFIG;
}

/**
 * Save compression settings to localStorage
 */
export function saveCompressionSettings(config: CompressionConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to save compression settings:', error);
  }
}

/**
 * Update compression mode
 */
export function updateCompressionMode(mode: CompressionMode): void {
  const config = loadCompressionSettings();
  config.mode = mode;
  saveCompressionSettings(config);
}

/**
 * Update compression threshold
 */
export function updateCompressionThreshold(thresholdKB: number): void {
  const config = loadCompressionSettings();
  config.threshold = thresholdKB * 1024;
  saveCompressionSettings(config);
}

/**
 * Update max dimensions
 */
export function updateMaxDimensions(width: number, height: number): void {
  const config = loadCompressionSettings();
  config.maxWidth = width;
  config.maxHeight = height;
  saveCompressionSettings(config);
}

/**
 * Update quality
 */
export function updateQuality(quality: number): void {
  const config = loadCompressionSettings();
  config.quality = Math.max(0, Math.min(1, quality));
  saveCompressionSettings(config);
}

/**
 * Reset to default settings
 */
export function resetCompressionSettings(): void {
  saveCompressionSettings(DEFAULT_COMPRESSION_CONFIG);
}

/**
 * Get compression mode description
 */
export function getCompressionModeDescription(mode: CompressionMode): string {
  switch (mode) {
    case 'none':
      return 'No compression - Images stored as-is';
    case 'auto':
      return 'Smart compression - Only compress images larger than threshold';
    case 'always':
      return 'Always compress - All images will be optimized';
    default:
      return 'Unknown mode';
  }
}

/**
 * Validate compression config
 */
export function validateCompressionConfig(config: Partial<CompressionConfig>): string[] {
  const errors: string[] = [];

  if (config.threshold !== undefined && config.threshold < 0) {
    errors.push('Threshold must be positive');
  }

  if (config.maxWidth !== undefined && config.maxWidth < 100) {
    errors.push('Max width must be at least 100px');
  }

  if (config.maxHeight !== undefined && config.maxHeight < 100) {
    errors.push('Max height must be at least 100px');
  }

  if (config.quality !== undefined && (config.quality < 0 || config.quality > 1)) {
    errors.push('Quality must be between 0 and 1');
  }

  return errors;
}
